<template>
    <div class="gallery" :class="{'gallery--expanded': expanded}">
        <div class="gallery__outer-container" @click="!expanded && toggleExpand()">
            <div class="gallery__photo-button" v-if="images.length > 1">
                <span>{{ currentSlide }} / {{ images.length }}</span>
            </div>
            <div class="gallery__slider">
                <div class="gallery__frame" ref="frame">
                    <ul class="gallery__slides" :style="stripPosition" ref="strip">
                        <li class="gallery__item" v-for="image in images"
                            :style="'background-image: url(' + image + ')'"> &#160;
                        </li>
                    </ul>
                </div>
            </div>

            <div class="gallery__buttons" v-if="images.length > 1">
                <div class="gallery__prev" @click.stop="previous">
                    <div class="gallery__button">
                        <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path>
                        </svg>
                    </div>
                </div>

                <div class="gallery__next" @click.stop="next">
                    <div class="gallery__button">
                        <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

export default defineComponent({
    props: {
        'images': {type: Array, required: true},
    },
    data() {
        return {
            currentSlide: 1,
            frameWidth: 0,
            stripWidth: 0,
            expanded: false,
            animating: false,
            keyboardHandler: null as ((event: KeyboardEvent) => void) | null,
        };
    },
    computed: {
        numberOfSlides(): number {
            return this.images.length;
        },
        stripPosition(): object {
            let offset = this.frameWidth * (this.currentSlide - 1);

            if (this.currentSlide === this.numberOfSlides) {
                offset = this.stripWidth - this.frameWidth;
            }

            if (this.animating) {
                return {
                    transition: 'all 0.5s',
                    transform: 'translateX(-' + offset + 'px)',
                };
            }

            return {
                transform: 'translateX(-' + offset + 'px)',
            };
        },
    },
    methods: {
        _refreshWidth() {
            setTimeout(() => {
                if (this.$refs.frame && this.$refs.strip) {
                    this.frameWidth = (this.$refs.frame as HTMLElement).clientWidth;
                    this.stripWidth = (this.$refs.strip as HTMLElement).scrollWidth;

                    setTimeout(() => {
                        this.animating = true;
                    }, 100);
                }
            }, 100);
        },
        toggleExpand() {
            this.animating = false;
            this.expanded = !this.expanded;
            this._refreshWidth();

            if (this.keyboardHandler) {
                window.removeEventListener('keyup', this.keyboardHandler);
                this.keyboardHandler = null;
            }

            if (this.expanded) {
                this.keyboardHandler = (event: KeyboardEvent) => {
                    if (event.code === 'ArrowLeft') {
                        this.previous();
                    }
                    if (event.code === 'ArrowRight') {
                        this.next();
                    }
                    if (event.code === 'Escape') {
                        this.toggleExpand();
                    }
                };
                window.addEventListener('keyup', this.keyboardHandler);
            }
        },
        previous() {
            if (this.currentSlide === 1) {
                this.currentSlide = this.numberOfSlides;
            } else {
                this.currentSlide -= 1;
            }
        },
        next() {
            if (this.currentSlide === this.numberOfSlides) {
                this.currentSlide = 1;
            } else {
                this.currentSlide += 1;
            }
        },
    },
    mounted() {
        this._refreshWidth();
        window.addEventListener('resize', this._refreshWidth);
    },
    beforeUnmount() {
        if (this.keyboardHandler) {
            window.removeEventListener('keyup', this.keyboardHandler);
            this.keyboardHandler = null;
        }
        window.removeEventListener('resize', this._refreshWidth);
    },
});
</script>

<style lang="scss">
@use '../../../sass/mixins';
@use '../../../sass/variables';

.gallery {
  &--expanded {
    background-color: rgba(0, 0, 0, 0.9);
    height: 100%;
    left: 0;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 3;

    .gallery__frame {
      background-color: transparent;
    }

    .gallery__item {
      background-size: contain;
      height: 80vh;
      margin-bottom: 10vh;
      margin-top: 10vh;
    }

    .gallery__outer-container {
      cursor: default;
    }
  }

  &__item {
    background-position: center;
    background-size: cover;
  }

  &__outer-container {
    cursor: pointer;
    position: relative;
  }

  &__slider {
    position: relative;
    width: 100%;
  }

  &__frame {
    background-color: transparent;
    font-size: 0;
    line-height: 0;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
  }

  &__slides {
    display: inline-block;
    width: 100%;

    &--center {
      .gallery__item {
        background-position: 50%;
        display: block;
        margin: 0 auto;
        width: 100%;
      }
    }
  }

  &__item {
    background-repeat: no-repeat;
    background-size: cover;
    display: inline-block;
    font-size: 16px;
    height: 133px;
    line-height: 1.45;
    min-height: 350px;
    position: relative;
    width: 100%;

    @include mixins.min-tablet {
      height: 396px;
    }

    @include mixins.min-large {
      height: 476px;
    }


    @include mixins.max-mobile {
      min-height: 250px;
    }

    @include mixins.min-large {
      min-height: 550px;
    }
  }

  &__buttons {
    bottom: 0;
    color: variables.$white;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;

    svg {
      fill: variables.$white;
      height: 60px;
      width: 60px;
    }
  }

  &__next {
    bottom: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  &__prev {
    bottom: 0;
    left: 0;
    position: absolute;
    top: 0;
  }

  &__button {
    align-items: center;
    cursor: pointer;
    display: flex;
    height: 100%;
    justify-content: center;
  }

  &__video-container {
    height: 0;
    padding-bottom: 56%;
    position: relative;
    width: 100%;
  }

  &__video {
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  &__header-embed {
    iframe {
      width: 100%;
    }
  }

  &__photo-button {
    align-items: center;
    background-color: variables.$navy;
    bottom: 15px;
    color: variables.$white;
    display: flex;
    left: 15px;
    padding: 5px;
    position: absolute;
    z-index: 1;

    > :first-child {
      margin-right: 5px;
    }
  }
}
</style>
